import { EyeIcon, PencilIcon, TrashIcon, UserPlusIcon, KeyIcon } from "@heroicons/react/24/outline";
import { AdminGate } from "../PermissionGate";
import ApiLoader from "../ui/ApiLoader";
import { DataTable } from "../common/DataTable";

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "customer_admin", label: "Customer Admin" },
  { value: "auditor", label: "Auditor" },
  { value: "security_analyst", label: "Security Analyst" },
  { value: "end_user", label: "End User" },
];

const UsersTabContent = ({ users2, getRoleColor, getStatusColor, handleViewUser, handleEditUser, handleDeleteUser, handleResetPassword, formatDate, userPagination, setUserPagination, loading, setShowRoleSelectionModal, removeRow, userFilters, setUserFilters, companyOptions }) => {
  const columnFilters = [
    userFilters?.email ? { id: "email", value: userFilters.email } : null,
    userFilters?.role ? { id: "role", value: userFilters.role } : null,
    userFilters?.company ? { id: "company", value: userFilters.company } : null,
  ].filter(Boolean);

  const handleColumnFiltersChange = (updater) => {
    const next = typeof updater === "function" ? updater(columnFilters) : updater;
    const byId = Object.fromEntries(next.map((f) => [f.id, f.value]));
    setUserFilters({
      email: byId.email || "",
      username: userFilters?.username || "",
      role: byId.role || "",
      company: byId.company || "",
    });
    setUserPagination((p) => ({ ...p, page: 1 }));
  };

  // NOTE: every cell below keeps the exact same text-size / font-weight
  // classes the original plain <table> used (text-sm, text-xs
  // font-medium, etc.) - only the table/filter mechanism changed, not
  // the typography.
  const columns = [
    {
      id: "email",
      accessorFn: (user) => user.email,
      header: "User",
      enableSorting: false,
      size: 260,
      meta: { filter: { variant: "text", label: "Email", placeholder: "Search by email" } },
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="flex items-center">
            {user.profile_photo ? (
              <img
                src={user.profile_photo}
                alt={user.firstname || "User"}
                className="w-10 h-10 shrink-0 rounded-full object-cover border border-gray-200"
              />
            ) : (
              <div className="w-10 h-10 shrink-0 bg-gray-300 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-gray-700">
                  {user.firstname ? user.firstname.split(' ').map(n => n[0]).join('') : "-"}
                </span>
              </div>
            )}
            <div className="ml-4 min-w-0">
              <div className="text-sm font-medium text-gray-900">{user.firstname}</div>
              <div className="text-sm text-gray-500">{user.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      id: "role",
      accessorFn: (user) => user.role,
      header: "Role",
      enableSorting: false,
      size: 160,
      meta: {
        filter: {
          variant: "select",
          label: "Role",
          placeholder: "All roles",
          options: ROLE_OPTIONS,
        },
      },
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mb-2 ${getRoleColor(row.original.role)}`}>
          <span className="relative z-10">{row.original.role.replace('_', ' ')}</span>
        </span>
      ),
    },
    {
      id: "company",
      accessorFn: (user) => user.company,
      header: "Company",
      enableSorting: false,
      size: 160,
      meta: {
        filter: {
          variant: "select",
          label: "Company",
          placeholder: "All companies",
          options: companyOptions || [],
        },
      },
      cell: ({ row }) => <span className="text-sm text-gray-900">{row.original.company}</span>,
    },
    {
      id: "status",
      accessorFn: (user) => user.status,
      header: "Status",
      enableSorting: false,
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(row.original.status)}`}>
          {row.original.status}
        </span>
      ),
    },
    {
      id: "last_login",
      accessorFn: (user) => user.last_login,
      header: "Last Login",
      enableSorting: false,
      cell: ({ row }) => <span className="text-sm text-gray-500">{formatDate(row.original.last_login)}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      size: 140,
      minSize: 140,
      maxSize: 140,
      enableResizing: false,
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="flex w-full items-center justify-start space-x-2">
            <button className="text-blue-600 hover:text-blue-900" onClick={() => handleViewUser(user)}>
              <EyeIcon className="w-4 h-4" />
            </button>
            <button className="text-green-600 hover:text-green-900" onClick={() => handleEditUser(user)}>
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              className="text-amber-600 hover:text-amber-900"
              title="Reset password"
              aria-label={`Reset password for ${user.email}`}
              onClick={() => handleResetPassword(user)}
            >
              <KeyIcon className="w-4 h-4" />
            </button>
            <button
              className="text-red-600 hover:text-red-900"
              onClick={() => handleDeleteUser(user.id, removeRow)}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">User Management</h3>
          <p className="text-gray-600">Manage user accounts and permissions</p>
        </div>
        <AdminGate>
          <button
            onClick={() => setShowRoleSelectionModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700"
          >
            <UserPlusIcon className="w-4 h-4 mr-2" />
            Add User
          </button>
        </AdminGate>
      </div>

      {loading && users2.length === 0 ? (
        <ApiLoader label="Loading users..." className="py-12" />
      ) : (
        <DataTable
          columns={columns}
          data={users2.filter(Boolean)}
          getRowId={(user) => String(user.id)}
          loading={loading}
          manualPagination
          rowCount={userPagination.count}
          pagination={{ pageIndex: (userPagination.page || 1) - 1, pageSize: userPagination.page_size }}
          onPaginationChange={(updater) => {
            const current = { pageIndex: (userPagination.page || 1) - 1, pageSize: userPagination.page_size };
            const next = typeof updater === "function" ? updater(current) : updater;
            setUserPagination((p) => ({ ...p, page: next.pageIndex + 1, page_size: next.pageSize }));
          }}
          enableColumnFilters
          manualFiltering
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          emptyMessage="No users found."
          enableColumnVisibility={false}
          enableColumnResizing
          maxBodyHeight="65vh"
        />
      )}
    </div>
  );
};
export default UsersTabContent;
